import { Link } from "@tanstack/react-router";
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { useApi } from "@/api/hooks";
import type { components } from "@/api/schema";
import { Skeleton } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatNumber, formatPct, trendClass } from "@/lib/format";

type Item = components["schemas"]["TickerItem"];

/** Pixels per second the rows move: slow enough to read a name and a number. */
const SPEED = 45;
/** Quotes are saved every 2 s, so asking more often gains nothing. */
const REFRESH_MS = 2_000;

/**
 * Two scrolling rows under the header: the headline indices, then the NIFTY 50
 * shares, both refreshed every 2 s. A row pauses under the pointer or keyboard
 * focus; with reduced motion it stays still and scrolls by hand.
 *
 * Until the first answer arrives both rows show at their final height with
 * shimmering placeholders, so the page below never jumps when the quotes land.
 */
export function MarketTicker({
  source = "/live/ticker",
}: {
  source?: "/live/ticker" | "/public/ticker";
}) {
  const q = useApi(source, undefined, {
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS,
  });
  if (q.isPending) return <TickerPlaceholder />;
  if (!q.data || (q.data.indices.length === 0 && q.data.stocks.length === 0))
    return null;
  const live = q.data.live;
  return (
    <div
      className="border-b border-border bg-surface text-xs"
      role="region"
      aria-label="Market ticker"
    >
      <Row
        label={live ? "Live" : "Close"}
        live={live}
        title="Indices"
        items={q.data.indices}
      />
      <Row
        label="NIFTY 50"
        live={live}
        title="NIFTY 50 shares"
        items={q.data.stocks}
        border
      />
    </div>
  );
}

/** The loading state: the same two rows, labels and borders, quotes shimmering. */
function TickerPlaceholder() {
  return (
    <div
      className="border-b border-border bg-surface text-xs"
      aria-busy="true"
      aria-label="Market ticker loading"
      role="region"
    >
      {["Close", "NIFTY 50"].map((label, i) => (
        <div
          key={label}
          className={cn(
            "flex h-8 items-center overflow-hidden",
            i > 0 && "border-t border-border",
          )}
        >
          <span className="flex h-full w-24 shrink-0 items-center border-r border-border px-3 font-semibold whitespace-nowrap text-muted">
            {label}
          </span>
          <div className="flex min-w-0 flex-1 items-center gap-8 overflow-hidden px-4">
            {Array.from({ length: 8 }, (_, n) => (
              <Skeleton key={n} className="h-3 w-32 shrink-0" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Row({
  label,
  live,
  title,
  items,
  border = false,
}: {
  label: string;
  live: boolean;
  title: string;
  items: Item[];
  border?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div
      className={cn(
        "group flex h-8 items-center overflow-hidden",
        border && "border-t border-border",
      )}
    >
      <span
        className={cn(
          "z-10 flex h-full w-24 shrink-0 items-center gap-1.5 border-r border-border bg-surface px-3 font-semibold whitespace-nowrap",
          live ? "text-up" : "text-muted",
        )}
      >
        {live ? (
          <span
            className="size-1.5 rounded-full bg-up motion-safe:animate-pulse-dot"
            aria-hidden
          />
        ) : null}
        {label}
      </span>
      <Marquee label={title}>
        {(copy) =>
          items.map((it) => (
            <Quote
              key={`${copy}-${it.kind}-${it.symbol}`}
              it={it}
              copy={copy > 0}
            />
          ))
        }
      </Marquee>
    </div>
  );
}

/**
 * Repeats its content until one copy is at least as wide as the row, then
 * shows that twice and moves left by half: an endless loop with no gap,
 * however few items there are.
 */
function Marquee({
  label,
  children,
}: {
  label: string;
  children: (copy: number) => ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const unit = useRef<HTMLUListElement>(null);
  const [repeat, setRepeat] = useState(1);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const boxW = box.current?.clientWidth ?? 0;
      const unitW = (unit.current?.scrollWidth ?? 0) / repeat;
      if (!boxW || !unitW) return;
      setRepeat(Math.max(1, Math.ceil(boxW / unitW)));
      setWidth(unitW * Math.max(1, Math.ceil(boxW / unitW)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [repeat, children]);

  const copies = Array.from({ length: repeat }, (_, n) => n);
  const seconds = Math.max(20, Math.round(width / SPEED));
  return (
    <div
      ref={box}
      className="min-w-0 flex-1 overflow-hidden motion-reduce:overflow-x-auto"
    >
      <div
        className="flex w-max motion-safe:animate-[ticker_var(--ticker-duration)_linear_infinite] group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]"
        style={{ "--ticker-duration": `${seconds}s` } as CSSProperties}
      >
        <ul
          ref={unit}
          className="flex shrink-0 items-center"
          aria-label={label}
        >
          {copies.map((n) => children(n))}
        </ul>
        {/* the second half of the loop; screen readers and the keyboard skip it */}
        <ul
          className="flex shrink-0 items-center motion-reduce:hidden"
          aria-hidden
        >
          {copies.map((n) => children(n + repeat))}
        </ul>
      </div>
    </div>
  );
}

function Quote({ it, copy }: { it: Item; copy: boolean }) {
  const body = (
    <>
      <span className="font-semibold text-fg">{it.symbol}</span>
      <span className="num text-muted">{formatNumber(it.ltp, 2)}</span>
      <span className={cn("num font-medium", trendClass(it.change_pct))}>
        {formatPct(it.change_pct, 2)}
      </span>
    </>
  );
  return (
    <li className="flex items-center whitespace-nowrap">
      {it.kind === "stock" ? (
        <Link
          to="/app/stocks/$symbol"
          params={{ symbol: it.symbol }}
          tabIndex={copy ? -1 : undefined}
          className="flex items-center gap-1.5 rounded px-3 py-1 hover:bg-surface-2"
        >
          {body}
        </Link>
      ) : (
        <span className="flex items-center gap-1.5 px-4 py-1">{body}</span>
      )}
    </li>
  );
}
